"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, FileText, Clock, MapPin, ArrowRight, PhoneCall, CircleCheck as CheckCircle2, Biohazard, Hammer, Droplets } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const relatedServices = [{
  id: "mold-remediation",
  title: "Mold Remediation",
  scope: "Contain airborne spores, remove active fungal colonies, and restore indoor air quality after lingering water intrusion.",
  to: "/MoldRemediationPage",
  badge: "Air Quality Control",
  icon: Droplets
}, {
  id: "sewage-cleanup",
  title: "Sewage Cleanup",
  scope: "Extract biohazard gray or black water safely, sanitize structural framing, and neutralize biological pathogens.",
  to: "/SewageCleanupPage",
  badge: "Hazardous Extraction",
  icon: Biohazard
}, {
  id: "reconstruction-design-build",
  title: "Reconstruction & Design-Build",
  scope: "Rebuild damaged drywall, flooring, electrical, and cabinetry with one accountable contractor from mitigation to occupancy.",
  to: "/ReconstructionDesignBuildPage",
  badge: "Turn-Key Rebuild",
  icon: Hammer
}];
const operationalPoints = [{
  id: "direct-technicians",
  title: "Direct regional response",
  description: "No third-party call centers or brokers. You speak directly with local dispatchers who deploy our own fully equipped service vehicles."
}, {
  id: "insurance-advocacy",
  title: "Direct insurance coordination",
  description: "We document moisture maps, psychrometric logs, and structural loss reports directly with your adjuster in real time."
}, {
  id: "end-to-end",
  title: "Integrated accountability",
  description: "Our certified technicians stay on your property from the initial midnight extraction through moisture clearance."
}];
export default function WhyChoose() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="why-choose-triserve-related-services" data-nav="dark" className="dark bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Technical Operational Bar */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border/80 pb-6 mb-12">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex size-2.5 rounded-full bg-primary animate-pulse" />
            <span className="font-mono text-xs font-semibold tracking-wider uppercase text-muted-foreground">
              Regional Operations: Maryland • DC • Northern Virginia
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs font-mono text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Clock className="size-3.5 text-primary" />
              24/7 Rapid Response
            </span>
            <span className="hidden sm:inline-block text-border">•</span>
            <span className="hidden sm:flex items-center gap-1.5">
              <ShieldCheck className="size-3.5 text-primary" />
              Direct Carrier Billing
            </span>
          </div>
        </div>

        {/* Upper Credibility Narrative */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-16 md:mb-24">
          {/* Text Column */}
          <motion.div variants={itemVariants} className="lg:col-span-7 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 self-start rounded-md bg-secondary/80 px-3 py-1 text-xs font-bold uppercase tracking-wider text-secondary-foreground mb-4 border border-border/40">
              <ShieldCheck className="size-3.5 text-accent" />
              Direct Contractor Standards
            </div>

            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-4">
              Local crews on site, direct insurance coordination
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6">
              When flooding strikes your property, TriServe sends dedicated regional crews directly to your door. We do not dispatch subcontracted handymen or filter emergencies through remote phone banks. From our first thermal inspection through complete dry-out, our teams operate our own commercial extraction units and work shoulder-to-shoulder with your insurance adjuster to approve line items without delay.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-border/60">
              {operationalPoints.map((point, i) => <div key={point.id} data-index={i} className="flex flex-col gap-1.5">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <h3 className="text-sm font-semibold text-foreground">
                      {point.title}
                    </h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-normal">
                    {point.description}
                  </p>
                </div>)}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild variant="default" size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold">
                <a href="tel:855-435-6631" className="flex items-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Call 855-435-6631</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted font-medium">
                <Link to="/ContactRequestServicePage">
                  <span>Request online dispatch</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Documentary Photo Column */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <div className="relative rounded-lg border border-border bg-card p-2 shadow-xl overflow-hidden group">
              <div className="relative aspect-[4/3] w-full overflow-hidden rounded-md bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/triserve_water_extraction_field_crew.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/triserve_water_extraction_field_crew.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/triserve_water_extraction_field_crew.png-wvc-sizes" alt="TriServe certified technicians placing industrial desiccant dehumidifiers and air movers inside a flood-damaged property" loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-[transform] duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent pointer-events-none" />

                {/* Technical Equipment Badge */}
                <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between rounded-md bg-background/90 backdrop-blur-xs px-3 py-2 border border-border text-xs">
                  <div className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-primary" />
                    <span className="font-mono text-foreground font-medium">Commercial LGR & Air Injectors Active</span>
                  </div>
                  <span className="font-mono text-muted-foreground text-[11px] hidden sm:inline">MD • DC • VA</span>
                </div>
              </div>

              <div className="px-3 py-2 flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <MapPin className="size-3.5 text-primary" />
                  Mid-Atlantic On-Scene Deployment
                </span>
                <span className="flex items-center gap-1.5">
                  <FileText className="size-3.5 text-accent" />
                  Itemized Xactimate Billing
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Lower Cross-Navigation Grid: Related Services */}
        <div className="pt-8 border-t border-border">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-wider text-primary font-semibold mb-2">
                Secondary Containment & Restoration
              </p>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                Related disaster restoration services
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Water migration frequently reveals hidden biohazards or requires complete structural rebuilds. Our teams manage each sequential phase under one contract.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.15
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedServices.map((service, i) => {
            const ServiceIcon = service.icon;
            return <motion.div key={service.id} data-index={i} variants={itemVariants}>
                  <Card className="h-full flex flex-col justify-between rounded-lg border border-border bg-card hover:border-primary/50 transition-[border-color,box-shadow] duration-200 shadow-sm hover:shadow-md">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex size-10 items-center justify-center rounded-md bg-secondary/30 text-primary border border-border/50">
                          <ServiceIcon className="size-5" />
                        </div>
                        <span className="font-mono text-[11px] uppercase tracking-wider font-semibold text-muted-foreground px-2 py-0.5 rounded-sm bg-muted">
                          {service.badge}
                        </span>
                      </div>
                      <CardTitle className="text-lg font-bold text-foreground">
                        {service.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="pb-4">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {service.scope}
                      </p>
                    </CardContent>

                    <CardFooter className="pt-2 border-t border-border/40">
                      <Button asChild variant="ghost" className="w-full justify-between px-2 text-sm font-semibold text-foreground hover:text-primary hover:bg-transparent">
                        <Link to={service.to} className="flex items-center w-full justify-between">
                          <span>Explore {service.title}</span>
                          <ArrowRight className="size-4 transition-transform group-hover:translate-x-1 text-primary" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </div>
    </section>;
}